import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { fetchData } from '../../../helpers/fetchData';
import CustomTable from '../../../shared/CustomTable';
import CustomPagination from '../../../shared/CustomPagination';
import CustomButton from '../../../shared/CustomButton';
import ReusableModal from '../../../shared/ReuseableModal';
import { EyeIcon } from '../../../utils/TableActionIcons';
import { MdAutoFixHigh } from 'react-icons/md';
import { formatDateTime } from '../../../utils/FormateDateTime';
import CustomChip from '../../../shared/CustomChip';
import { ProgressBar } from 'primereact/progressbar';
import { useGetAutoCorrectionBatchesQuery } from '../../../redux/api/modules/batchesApi';

const AutoCorrectionBatchesTab = () => {
  const { t } = useTranslation();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [pollingJobId, setPollingJobId] = useState(null);
  const [expandedFailures, setExpandedFailures] = useState(false);

  const [filters, setFilters] = useState({
    page: 1,
    per_page: 20,
    status: '',
  });

  const {
    data,
    isLoading: queryLoading,
    isFetching: queryFetching,
    refetch,
  } = useGetAutoCorrectionBatchesQuery(filters);

  const loading = queryLoading || queryFetching;

  const fetchJobStatus = async (jobId) => {
    try {
      const resp = await fetchData(
        'GET',
        `/v3/invoices/audit/auto-correct/job/${jobId}`
      );
      if (resp) {
        setSelectedJob(resp);
      }
    } catch (error) {
      console.error('Error fetching job status:', error);
      toast.error(t('Failed to fetch job status'));
      setPollingJobId(null);
    }
  };

  const onPageChange = (newPage, newRowsPerPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
      per_page: newRowsPerPage,
    }));
  };

  const handleViewDetails = async (job) => {
    setSelectedJob(job);
    setShowDetailModal(true);
    if (job.status === 'processing' || job.status === 'queued') {
      setPollingJobId(job.job_id);
      fetchJobStatus(job.job_id);
    } else {
      fetchJobStatus(job.job_id);
    }
  };

  const getStatusChipType = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'warning';
      case 'queued':
      case 'pending':
        return 'default';
      case 'failed':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      queued: { label: 'Queued', className: 'bg-gray-500' },
      processing: { label: 'Processing', className: 'bg-blue/80' },
      completed: { label: 'Completed', className: 'bg-green-500' },
      failed: { label: 'Failed', className: 'bg-red-500' },
      stopped: { label: 'Stopped', className: 'bg-orange-500' },
    };
    const config = statusConfig[status?.toLowerCase()] || {
      label: status,
      className: 'bg-gray-500',
    };
    return (
      <span
        className={`px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${config.className}`}
      >
        {t(config.label)}
      </span>
    );
  };

  const action = {
    header: t('Actions'),
    body: (rowData) => (
      <div className="flex gap-2 items-center">
        <EyeIcon
          onClick={() => handleViewDetails(rowData)}
          title={t('View Job Details')}
        />
      </div>
    ),
  };

  const columns = [
    action,
    {
      header: t('Progress'),
      body: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-xs">
            {(row?.progress_percentage || 0).toFixed(0)}%
          </span>
        </div>
      ),
    },
    {
      header: t('Success'),
      body: (row) => (
        <span className="text-green-600 font-semibold">
          {row.success_count || 0}
        </span>
      ),
    },
    {
      header: t('Failed'),
      body: (row) => (
        <span className="text-red-600 font-semibold">
          {row.failed_count || 0}
        </span>
      ),
    },
    {
      header: t('Items Corrected'),
      body: (row) => <span>{row.items_corrected || 0}</span>,
    },
    {
      header: t('Issues Resolved'),
      body: (row) => (
        <span className="text-green-600 font-medium">
          {row.issues_resolved || 0}
        </span>
      ),
    },
    {
      header: t('Created At'),
      body: (row) => formatDateTime(row?.created_at) || '-',
    },
    {
      header: t('Completed At'),
      body: (row) =>
        row?.completed_at ? formatDateTime(row?.completed_at) : '-',
    },
    {
      header: t('Status'),
      body: (row) => (
        <CustomChip
          label={
            row?.status?.charAt(0).toUpperCase() + row?.status?.slice(1) ||
            'Unknown'
          }
          chipType={getStatusChipType(row?.status)}
          className="h-[22px] rounded-full px-2 py-1"
        />
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-end mb-2">
        <CustomButton
          title={t('Refresh')}
          buttonType="secondary"
          icon="pi pi-refresh"
          disabled={loading}
          loading={loading}
          className="w-fit h-[35px]"
          onClick={() => refetch()}
        />
      </div>

      <CustomTable
        data={data?.jobs || []}
        isLoading={loading}
        columns={columns}
        emptyMessage={t('No auto-correction jobs found')}
      />

      <CustomPagination
        currentPage={data?.page || filters?.page || 1}
        rowsPerPage={data?.per_page || filters?.per_page || 20}
        totalRecords={data?.total_count || 0}
        totalPages={data?.total_pages || 1}
        onPageChange={onPageChange}
      />

      <ReusableModal
        isVisible={showDetailModal}
        title={t('Auto-Correction Job Details')}
        HeaderIcon={MdAutoFixHigh}
        onClose={() => {
          setShowDetailModal(false);
          setPollingJobId(null);
        }}
        className="w-full lg:w-[1000px]"
        position="top"
      >
        {selectedJob && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">{t('Job Status')}</h3>
                <p className="text-sm text-gray-500 font-mono">{selectedJob.job_id}</p>
              </div>
              {getStatusBadge(selectedJob.status)}
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>{t('Progress')}</span>
                <span>{(selectedJob.progress_percentage || 0).toFixed(1)}%</span>
              </div>
              <ProgressBar value={selectedJob.progress_percentage || 0} showValue={false} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-blue/10 p-2 rounded-lg"><p className="text-xs text-gray-600">{t('Total')}</p><p className="text-xl font-bold text-blue/60">{selectedJob.total_audits || 0}</p></div>
              <div className="bg-gray-100 p-2 rounded-lg"><p className="text-xs text-gray-600">{t('Processed')}</p><p className="text-xl font-bold text-gray-600">{selectedJob.processed_count || 0}</p></div>
              <div className="bg-green-50 p-2 rounded-lg"><p className="text-xs text-gray-600">{t('Success')}</p><p className="text-xl font-bold text-green-600">{selectedJob.success_count || 0}</p></div>
              <div className="bg-red-50 p-2 rounded-lg"><p className="text-xs text-gray-600">{t('Failed')}</p><p className="text-xl font-bold text-red-600">{selectedJob.failed_count || 0}</p></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-600">
              <span>{t('Items Corrected')}: <b className="text-blue-600">{selectedJob.items_corrected || 0}</b></span>
              <span>{t('Issues Resolved')}: <b className="text-green-600">{selectedJob.issues_resolved || 0}</b></span>
              <span>{t('Audits Fully Resolved')}: <b className="text-green-600">{selectedJob.audits_fully_resolved || 0}</b></span>
            </div>
            {selectedJob.failure_reasons && Object.keys(selectedJob.failure_reasons).length > 0 && (
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <button
                  type="button"
                  onClick={() => setExpandedFailures(!expandedFailures)}
                  className="w-full flex justify-between items-center text-sm font-semibold text-red-700 hover:text-red-900"
                >
                  <span>{t('Failure Reasons')} ({Object.keys(selectedJob.failure_reasons).length})</span>
                  <span>{expandedFailures ? '▲' : '▼'}</span>
                </button>
                {expandedFailures && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {Object.entries(selectedJob.failure_reasons).map(([reason, count]) => (
                      <div key={reason} className="flex justify-between text-xs text-red-600 py-0.5 border-b border-red-100 last:border-0">
                        <span className="truncate mr-2" title={reason}>{reason}</span>
                        <span className="font-mono font-bold shrink-0">{count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <CustomButton
                title={t('Close')}
                buttonType="secondary"
                onClick={() => {
                  setShowDetailModal(false);
                  setPollingJobId(null);
                }}
              />
            </div>
          </div>
        )}
      </ReusableModal>
    </div>
  );
};

export default AutoCorrectionBatchesTab;
